import { z } from "zod";
/**
 * Factory function to create ExpenseSplitValidator with translations
 */
export const createExpenseSplitValidator = (t) => z.object({
    partnerId: z
        .number(t("validation.expenses.split.partnerRequired"))
        .int(t("validation.expenses.split.partnerRequired"))
        .positive(t("validation.expenses.split.partnerRequired")),
    splitType: z.literal("custom"),
    customPercentage: z
        .number(t("validation.expenses.split.percentageRequired"))
        .min(0, t("validation.expenses.split.percentageMin"))
        .max(100, t("validation.expenses.split.percentageMax")),
});
/**
 * Factory function to create CreateExpenseValidator with translations
 */
export const createCreateExpenseValidator = (t) => {
    const ExpenseSplitValidator = createExpenseSplitValidator(t);
    return z
        .object({
        name: z
            .string()
            .min(1, t("validation.expenses.nameRequired"))
            .max(255, t("validation.expenses.nameTooLong")),
        description: z.string().max(500, t("validation.expenses.descriptionTooLong")).optional(),
        amount: z.preprocess((value) => {
            if (value === "" || value === null || value === undefined) {
                return 0;
            }
            const numValue = parseFloat(value);
            return !isNaN(numValue) ? numValue : 0;
        }, z
            .number(t("validation.expenses.amountNumber"))
            .positive(t("validation.expenses.amountPositive"))),
        recurring: z
            .enum(["one-time", "monthly", "quarterly", "yearly", "weekly"])
            .default("one-time"),
        isVirtual: z.boolean().default(false).optional(),
        isIncome: z.boolean().default(false).optional(),
        splitPreference: z.enum(["equitable", "equal", "custom"]).default("equitable").optional(),
        nextPaymentDate: z.preprocess((val) => {
            if (val === "" || val === null || val === undefined) {
                return new Date().toISOString();
            }
            if (typeof val === "string") {
                try {
                    const date = new Date(val);
                    if (!isNaN(date.getTime())) {
                        return date.toISOString();
                    }
                }
                catch (e) {
                    return new Date().toISOString();
                }
            }
            return val;
        }, z.string().datetime({
            message: t("validation.expenses.dateInvalid"),
        })),
        expenseCategoryId: z
            .number(t("validation.expenses.categoryRequired"))
            .int(t("validation.expenses.categoryRequired"))
            .positive(t("validation.expenses.categoryRequired")),
        splits: z.array(ExpenseSplitValidator).optional(),
    })
        .refine((data) => {
        if (data.recurring !== "one-time" && !data.nextPaymentDate) {
            return false;
        }
        return true;
    }, {
        message: t("validation.expenses.nextPaymentDateRequired"),
        path: ["nextPaymentDate"],
    })
        .refine((data) => {
        if (data.splitPreference === "custom") {
            if (!data.splits || data.splits.length === 0) {
                return false;
            }
        }
        return true;
    }, {
        message: t("validation.expenses.splitsRequired"),
        path: ["splits"],
    })
        .refine((data) => {
        if (data.splits && data.splits.length > 0) {
            if (data.splitPreference !== "custom") {
                return false;
            }
            const totalPercentage = data.splits.reduce((sum, split) => sum + split.customPercentage, 0);
            if (Math.abs(totalPercentage - 100) > 0.01) {
                return false;
            }
        }
        return true;
    }, {
        message: t("validation.expenses.splitsTotal100"),
        path: ["splits"],
    });
};
/**
 * Factory function to create UpdateExpenseValidator with translations
 */
export const createUpdateExpenseValidator = (t) => {
    const ExpenseSplitValidator = createExpenseSplitValidator(t);
    return z
        .object({
        name: z
            .string()
            .min(1, t("validation.expenses.nameRequired"))
            .max(255, t("validation.expenses.nameTooLong"))
            .optional(),
        description: z
            .union([
            z.string().max(500, t("validation.expenses.descriptionTooLong")),
            z.null(),
            z.undefined(),
        ])
            .optional()
            .transform((val) => val ?? ""),
        amount: z.preprocess((value) => {
            if (value === "" || value === null || value === undefined) {
                return 0;
            }
            const numValue = parseFloat(value);
            return !isNaN(numValue) ? numValue : 0;
        }, z
            .number(t("validation.expenses.amountNumber"))
            .positive(t("validation.expenses.amountPositive"))
            .optional()),
        recurring: z.enum(["one-time", "monthly", "quarterly", "yearly", "weekly"]).optional(),
        isVirtual: z.boolean().optional(),
        isIncome: z.boolean().optional(),
        splitPreference: z.enum(["equitable", "equal", "custom"]).optional(),
        nextPaymentDate: z.preprocess((val) => {
            if (val === "" || val === null || val === undefined) {
                return new Date().toISOString();
            }
            if (typeof val === "string") {
                try {
                    const date = new Date(val);
                    if (!isNaN(date.getTime())) {
                        return date.toISOString();
                    }
                }
                catch (e) {
                    return new Date().toISOString();
                }
            }
            return val;
        }, z
            .string()
            .datetime({
            message: t("validation.expenses.dateInvalid"),
        })
            .optional()),
        expenseCategoryId: z
            .number(t("validation.expenses.categoryRequired"))
            .int(t("validation.expenses.categoryRequired"))
            .positive(t("validation.expenses.categoryRequired"))
            .optional(),
        splits: z.array(ExpenseSplitValidator).optional(),
    })
        .refine((data) => {
        if (data.recurring && data.recurring !== "one-time" && !data.nextPaymentDate) {
            return false;
        }
        return true;
    }, {
        message: t("validation.expenses.nextPaymentDateRequired"),
        path: ["nextPaymentDate"],
    })
        .refine((data) => {
        if (data.splitPreference === "custom") {
            if (!data.splits || data.splits.length === 0) {
                return false;
            }
        }
        return true;
    }, {
        message: t("validation.expenses.splitsRequired"),
        path: ["splits"],
    })
        .refine((data) => {
        if (data.splits && data.splits.length > 0) {
            if (data.splitPreference !== "custom") {
                return false;
            }
            const totalPercentage = data.splits.reduce((sum, split) => sum + split.customPercentage, 0);
            if (Math.abs(totalPercentage - 100) > 0.01) {
                return false;
            }
        }
        return true;
    }, {
        message: t("validation.expenses.splitsTotal100"),
        path: ["splits"],
    });
};
/**
 * Factory function to create BulkCreateExpenseValidator with translations
 */
export const createBulkCreateExpenseValidator = (t) => {
    const CreateExpenseValidator = createCreateExpenseValidator(t);
    return z
        .array(CreateExpenseValidator)
        .min(1, t("validation.expenses.bulkMin"))
        .max(50, t("validation.expenses.bulkMax"));
};
